/**
 * useAdminUsers - Admin-Userverwaltung mit Impersonation (v1-Paritaet)
 *
 * GET  /api/v2/admin/users?q=&premium=active|expired&page=&per_page=
 * POST /api/v2/admin/users/:id/impersonate
 */
import { useQuery, useMutation } from '@tanstack/react-query'
import { useAuthStore } from '../../stores/authStore'
import { useUserStore } from '../../stores/userStore'
import { backupAdminSession, clearImpersonationBackup } from '../../services/impersonation'
import { toast } from '../../components/Toast/useToast'
import i18n from '../../i18n'

export type AdminUser = {
  id: number
  email: string
  created_at: string
  last_sign_in_at: string | null
  confirmed_at: string | null
  plan: string
  is_premium: boolean
  premium_until: string | null
  hotspots_count: number
}

export type AdminUsersPagy = {
  page: number
  pages: number
  count: number
  items: number
}

type AdminUsersResponse = {
  data: AdminUser[]
  meta: { pagy: AdminUsersPagy }
}

function getAuthToken(): string | null {
  return useAuthStore.getState().accessToken ?? null
}

export type PremiumFilter = "active" | "expired" | undefined

async function fetchAdminUsers(q: string, page: number, premium: PremiumFilter): Promise<AdminUsersResponse> {
  const baseUrl = import.meta.env.VITE_API_BASE_URL || ""
  const token = getAuthToken()
  const params = new URLSearchParams()
  params.set("page", String(page))
  params.set("per_page", "25")
  if (q) params.set("q", q)
  if (premium) params.set("premium", premium)
  const url = baseUrl + "/api/v2/admin/users?" + params.toString()
  const headers: Record<string, string> = {}
  if (token) headers.Authorization = "Bearer " + token
  const response = await fetch(url, { headers })
  if (!response.ok) {
    throw new Error(i18n.t("adminUsers:toast.loadFailed", { status: response.status }))
  }
  return response.json()
}

export function useAdminUsers(q: string, page: number, premium: PremiumFilter = undefined) {
  const query = useQuery<AdminUsersResponse>({
    queryKey: ["admin-users", q, page, premium],
    queryFn: () => fetchAdminUsers(q, page, premium),
    retry: false,
    staleTime: 30000,
  })
  return {
    users: query.data?.data,
    pagy: query.data?.meta?.pagy,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  }
}

export type ImpersonateTokens = {
  access_token: string
  refresh_token: string
  expires_in: number
  token_type: string
}

async function impersonateUser(id: number): Promise<ImpersonateTokens> {
  const baseUrl = import.meta.env.VITE_API_BASE_URL || ""
  const token = getAuthToken()
  const url = baseUrl + "/api/v2/admin/users/" + id + "/impersonate"
  const headers: Record<string, string> = {}
  if (token) headers.Authorization = "Bearer " + token
  const response = await fetch(url, { method: "POST", headers })
  if (!response.ok) {
    const json = await response.json().catch(() => ({} as { error?: string }))
    throw new Error(json.error ?? i18n.t("adminUsers:toast.impersonateFailed"))
  }
  return response.json()
}

export function useImpersonateUser() {
  return useMutation({
    mutationFn: async (id: number) => {
      const adminEmail = useUserStore.getState().user?.email ?? ""
      backupAdminSession(adminEmail)
      try {
        const tokens = await impersonateUser(id)
        useAuthStore.getState().setTokens(tokens.access_token, tokens.refresh_token, tokens.expires_in)
        return tokens
      } catch (err) {
        clearImpersonationBackup()
        throw err
      }
    },
    onSuccess: () => {
      window.location.assign("/dashboard")
    },
    onError: (err: Error) => {
      toast.error(err.message || i18n.t("adminUsers:toast.impersonateFailed"))
    },
  })
}
